import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  MapPin, 
  Truck, 
  CheckCircle, 
  Clock, 
  Package, 
  Navigation,
  Calendar,
  Building2,
  Phone,
  AlertCircle,
  Loader2,
  Map as MapIcon
} from "lucide-react";
import { Button } from "../components/ui/button";
import ShippingRouteMap from "../components/ShippingRouteMap";
import { API_URL } from "../config/api";
import { RoketPublicWidget } from "../components/RoketPublicWidget";


const STATUS_CONFIG = {
  draft: { label: "Order Created", icon: Package, color: "bg-gray-100 text-gray-600" },
  sent: { label: "Order Sent", icon: Package, color: "bg-blue-100 text-blue-600" },
  confirmed: { label: "Confirmed", icon: CheckCircle, color: "bg-cyan-100 text-cyan-600" },
  pickup_pending: { label: "Awaiting Pickup", icon: Clock, color: "bg-yellow-100 text-yellow-600" },
  picked_up: { label: "Picked Up", icon: Truck, color: "bg-orange-100 text-orange-600" },
  in_transit: { label: "In Transit", icon: Navigation, color: "bg-purple-100 text-purple-600" },
  delivered: { label: "Delivered", icon: CheckCircle, color: "bg-green-100 text-green-600" },
  pod_requested: { label: "Awaiting POD", icon: Package, color: "bg-indigo-100 text-indigo-600" },
  pod_received: { label: "POD Received", icon: CheckCircle, color: "bg-emerald-100 text-emerald-600" },
  completed: { label: "Completed", icon: CheckCircle, color: "bg-green-200 text-green-700" },
};

export default function PublicTrackingPage() {
  const { trackingId, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    const fetchTracking = async () => {
      try {
        // Use XMLHttpRequest to bypass any fetch interceptors
        const data = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', `${API_URL}/public-tracking/${trackingId}/${token}`, true);
          xhr.setRequestHeader('Accept', 'application/json');
          xhr.onload = function() {
            if (this.status >= 200 && this.status < 300) {
              try {
                resolve(JSON.parse(this.responseText));
              } catch (e) {
                reject(new Error("Invalid response format"));
              }
            } else {
              try {
                const errorData = JSON.parse(this.responseText);
                reject(new Error(errorData.detail || "Tracking link invalid or expired"));
              } catch (e) {
                reject(new Error("Tracking link invalid or expired"));
              }
            }
          };
          xhr.onerror = function() {
            reject(new Error("Network error - unable to fetch tracking information"));
          };
          xhr.send();
        });
        
        setTrackingData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTracking();
    
    // Refresh every 5 minutes for live updates
    const interval = setInterval(fetchTracking, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [trackingId, token]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-900 to-blue-950 flex items-center justify-center p-4">
        <RoketPublicWidget />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
          <p className="text-white/80">Loading tracking information...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-900 to-blue-950 flex items-center justify-center p-4">
        <RoketPublicWidget />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Tracking Not Found</h2>
          <p className="text-gray-600">{error}</p>
          <p className="text-gray-500 text-sm mt-4">
            This tracking link may have expired or is invalid.
          </p>
        </motion.div>
      </div>
    );
  }

  const status = STATUS_CONFIG[trackingData?.workflow_status] || STATUS_CONFIG.draft;
  const StatusIcon = status.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 to-blue-950">
      <RoketPublicWidget />
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-white">Europacific</h1>
              <p className="text-white/60 text-sm">Shipment Tracking</p>
            </div>
            <div className="text-right">
              <p className="text-white/60 text-xs">PF Number</p>
              <p className="text-white font-mono font-semibold">{trackingData?.pf_number || trackingData?.order_number}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-6 mb-6"
        >
          {/* PF Number & Transport Order Reference */}
          <div className="mb-6 pb-4 border-b">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">PF Number</p>
                <p className="text-lg font-bold text-blue-900">{trackingData?.pf_number || "-"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Transport Order Reference</p>
                <p className="text-lg font-bold text-gray-700">{trackingData?.order_number || "-"}</p>
              </div>
            </div>
          </div>

          {/* Status Display */}
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-14 h-14 rounded-full ${status.color} flex items-center justify-center`}>
              <StatusIcon className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{status.label}</h2>
              {trackingData?.current_location && (
                <p className="text-gray-600 flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {trackingData.current_location}
                </p>
              )}
            </div>
          </div>

          {/* Route Summary */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span>Origin</span>
              </div>
              <p className="font-semibold text-gray-900">{trackingData?.origin_address || "-"}</p>
              {trackingData?.planned_pickup_date && (
                <p className="text-sm text-gray-500 mt-1">
                  <Calendar className="w-3 h-3 inline mr-1" />
                  {formatDate(trackingData.planned_pickup_date)}
                </p>
              )}
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span>Destination</span>
              </div>
              <p className="font-semibold text-gray-900">{trackingData?.destination_address || "-"}</p>
              {trackingData?.planned_delivery_date && (
                <p className="text-sm text-gray-500 mt-1">
                  <Calendar className="w-3 h-3 inline mr-1" />
                  {formatDate(trackingData.planned_delivery_date)}
                </p>
              )}
            </div>
          </div>

          {/* Map Toggle */}
          <Button
            variant="outline"
            onClick={() => setShowMap(!showMap)}
            className="w-full gap-2"
            data-testid="toggle-map-btn"
          >
            <MapIcon className="w-4 h-4" />
            {showMap ? "Hide Map" : "Show on Map"}
          </Button>

          {/* Map */}
          {showMap && (trackingData?.origin_city || trackingData?.loading_place) && (trackingData?.destination_city || trackingData?.unloading_place) && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="mt-4 rounded-xl overflow-hidden border border-gray-200"
            >
              <ShippingRouteMap
                height="300px"
                originPort={trackingData.origin_city || trackingData.loading_place}
                destinationPort={trackingData.destination_city || trackingData.unloading_place}
                currentLocation={trackingData.driver_location?.address || trackingData.current_location}
                trackingPings={trackingData.tracking_pings || (trackingData.driver_location ? [{
                  location: trackingData.driver_location,
                  responded_at: trackingData.driver_location.timestamp,
                  status: "responded",
                }] : [])}
                showLabels={true}
                animateShip={false}
              />
              {trackingData.driver_location && (
                <div className="bg-amber-50 border-t border-amber-200 p-3">
                  <p className="text-sm text-amber-800 flex items-center gap-2">
                    <Navigation className="w-4 h-4" />
                    <span>
                      Last location update: {formatDate(trackingData.driver_location.timestamp)}
                    </span>
                  </p>
                </div>
              )}
            </motion.div>
          )}
          
          {/* Fallback message when no route data */}
          {showMap && !(trackingData?.origin_city || trackingData?.loading_place) && !(trackingData?.destination_city || trackingData?.unloading_place) && (
            <div className="mt-4 p-4 bg-gray-100 rounded-xl text-center text-sm text-gray-500">
              Route information not available for this shipment
            </div>
          )}
        </motion.div>

        {/* 2026-05-16 — Embedded vendor GPS tracker (page-in-page).
            When the truck carrier provides their own GPS link, staff
            adds it to the TO and customers see the live vendor map
            inside our branded shell. Falls back to "Open in new tab"
            if the vendor blocks iframe embedding via X-Frame-Options. */}
        {trackingData?.vendor_tracking_url && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
            data-testid="vendor-tracking-embed"
          >
            <div className="flex items-center justify-between bg-gradient-to-r from-slate-50 to-blue-50 border-b px-4 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <Navigation className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">
                    {trackingData.vendor_tracking_label || "Live GPS Tracking"}
                  </p>
                  {trackingData.vendor_tracking_provider && (
                    <p className="text-[11px] text-slate-500 truncate">
                      Powered by {trackingData.vendor_tracking_provider}
                    </p>
                  )}
                </div>
              </div>
              <a
                href={trackingData.vendor_tracking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium flex-shrink-0"
                data-testid="vendor-tracking-open-tab"
              >
                Open ↗
              </a>
            </div>
            <div className="relative bg-slate-100" style={{ height: "520px" }}>
              <iframe
                src={trackingData.vendor_tracking_url}
                title="Vendor GPS tracker"
                className="w-full h-full"
                referrerPolicy="no-referrer-when-downgrade"
                allow="geolocation; fullscreen; clipboard-read; clipboard-write"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                data-testid="vendor-tracking-iframe"
              />
              <p className="absolute bottom-2 right-2 text-[10px] text-slate-400 bg-white/80 backdrop-blur px-2 py-0.5 rounded">
                Doesn't load? Tap <b>Open ↗</b> above.
              </p>
            </div>
          </motion.div>
        )}

        {/* Timeline */}
        {trackingData?.status_updates?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-xl p-6 mb-6"
          >
            <h3 className="font-semibold text-gray-900 mb-4">Tracking History</h3>
            <div className="space-y-4">
              {trackingData.status_updates.slice().reverse().map((update, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
                    {index < trackingData.status_updates.length - 1 && (
                      <div className="w-0.5 h-full bg-gray-200 my-1"></div>
                    )}
                  </div>
                  <div className="flex-1 pb-4">
                    <p className="font-medium text-gray-900">{update.type?.replace(/_/g, ' ').toUpperCase() || 'Status Update'}</p>
                    {update.parsed_data?.current_location && (
                      <p className="text-sm text-gray-600">{update.parsed_data.current_location}</p>
                    )}
                    <p className="text-xs text-gray-400">{formatDate(update.parsed_at || update.updated_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Cargo Info */}
        {trackingData?.cargo_description && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-xl p-6 mb-6"
          >
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Cargo Information
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Description</p>
                <p className="font-medium text-gray-900">{trackingData.cargo_description}</p>
              </div>
              {trackingData.weight && (
                <div>
                  <p className="text-gray-500">Weight</p>
                  <p className="font-medium text-gray-900">{trackingData.weight} kg</p>
                </div>
              )}
              {trackingData.container_number && (
                <div>
                  <p className="text-gray-500">Container</p>
                  <p className="font-medium text-gray-900 font-mono">{trackingData.container_number}</p>
                </div>
              )}
              {trackingData.reference && (
                <div>
                  <p className="text-gray-500">Reference</p>
                  <p className="font-medium text-gray-900">{trackingData.reference}</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center"
        >
          <p className="text-white/80 text-sm mb-2">Questions about your shipment?</p>
          <p className="text-white font-semibold flex items-center justify-center gap-2">
            <Phone className="w-4 h-4" />
            +386 5 662 6380
          </p>
          <p className="text-white/60 text-xs mt-4">
            Europacific Logistika d.o.o.
          </p>
        </motion.div>

        {/* GDPR Disclaimer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-6 text-center"
        >
          <p className="text-white/40 text-xs leading-relaxed max-w-2xl mx-auto">
            <strong>GPS Tracking Notice:</strong> Real-time location tracking is subject to driver consent 
            and may not always be available. Under GDPR Art. 6(1)(b)(f), location data is processed for 
            contractual service delivery. The driver retains the right to decline location sharing. 
            Location accuracy depends on device and network conditions.
          </p>
          <p className="text-white/30 text-xs mt-2">
            For data protection inquiries: privacy@europacific.com
          </p>
        </motion.div>
      </div>
    </div>
  );
}
